import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { Text, Box, clsx, Flex } from '@mantine/core';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';
import { type ResizingMode, type EntriesDetails } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import { CurrencyConvertor } from '~/components/utils/formatters';
import ContestCountdown from '~/components/utils/ContestCountdown';
import { PageHeaderContent } from '~/domains/common/components/PageHeader/PageHeader';
import { Link } from '~/domains/common/components/Link/Link';
import { Routes } from '~/domains/common/constants/routes';
import dt from '~/testing';
import { getDeadlineFormat } from '../../utils/deadlineFormat';
import getContestMaxEntriesLabel from '../../../gamecenter/domains/common/utils/getContestMaxEntriesLabel';
import type { Contest } from '../../../services/interfaces/contest.interface';

import useStyles from './ContestHeader.styles';
import { useAdditionalPayoutSettings } from '../../../common/hooks/useAdditionalPayoutSettings';
import PrizeBadge from '../../../common/components/PrizeBadge/PrizeBadge';

type ContestHeaderContentProps = {
  deadline: Date;
  isEntryDeadlinePending?: boolean;
  entries: EntriesDetails;
  fee: number;
  prize: number;
  username: string;
  ownerId: string;
  createdByAdmin: boolean;
  isSidePanel?: boolean;
  resizingMode?: ResizingMode;
  isEntertainmentOnly?: boolean;
  contest: Contest;
};

export function ContestHeaderContent({
  isEntertainmentOnly,
  entries,
  fee,
  prize,
  deadline,
  isEntryDeadlinePending,
  username,
  ownerId,
  createdByAdmin,
  isSidePanel,
  resizingMode,
  contest,
}: ContestHeaderContentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.xs });
  const { t } = useTranslation('contest');
  const {
    type,
    isEnabled: isGuaranteedPlusEnabled,
    hasPrizeIncreased,
  } = useAdditionalPayoutSettings(contest);

  const { classes } = useStyles({ isSidePanel });
  const maxEntries = getContestMaxEntriesLabel(entries);

  const contestInfo = [
    {
      id: 0,
      label: 'Creator',
      value: (
        <Text component="span" display="block">
          {username ? (
            <Link href={Routes.channel(username)} passHref>
              {createdByAdmin ? t('detail.about.info.adminCreator') : username}
            </Link>
          ) : (
            t('detail.about.info.adminCreator')
          )}
        </Text>
      ),
      isHiddenOnMobile: isMobile,
    },
    !isEntertainmentOnly && {
      id: 1,
      label: (
        <Flex align="center" gap={theme.other.spacing._6}>
          {t('detail.header.info.prize')}
          <PrizeBadge
            resizingMode={resizingMode}
            isGuaranteedPlusEnabled={isGuaranteedPlusEnabled}
            additionalPayoutType={type}
            iconSize={16}
          />
        </Flex>
      ),
      value: (
        <Text color={hasPrizeIncreased ? theme.colors.gold[0] : undefined}>
          {CurrencyConvertor(prize / 100)}
        </Text>
      ),
      isPrimary: true,
    },
    !isEntertainmentOnly && {
      id: 2,
      label: t('detail.header.info.entry'),
      value: CurrencyConvertor(fee / 100),
    },
    {
      id: 3,
      label: t('detail.header.info.entries'),
      value: `${entries.filled} / ${maxEntries}`,
      isHiddenOnMobile: isSidePanel,
    },
    {
      id: 4,
      label: t('detail.header.info.deadline'),
      value: isEntryDeadlinePending ? (
        t('detail.firstTeeTimeTbd')
      ) : (
        <ContestCountdown contestStart={deadline} color={null} fontSize={null} />
      ),
      isHiddenOnMobile:
        isMobile ||
        isSidePanel ||
        (!isEntryDeadlinePending && new Date(deadline.toString()) <= new Date()),
    },
  ].filter(Boolean);

  return (
    <PageHeaderContent
      data-test-id={dt.contestDetail.page.header.content}
      isSidePanel={isSidePanel}
    >
      {contestInfo.map(
        ({ label, id, value, isPrimary, isHiddenOnMobile }) =>
          !isHiddenOnMobile && (
            <Box className={classes.flex} key={id}>
              <Text
                className={classes.contestInfo}
                color={isPrimary ? theme.colors.cyan[6] : 'inherit'}
                m={0}
                component="h5"
                fw={600}
              >
                {value}
              </Text>
              <Text component="span" className={clsx(classes.dimmedText, classes.contestInfo)}>
                {label}
              </Text>
            </Box>
          )
      )}
    </PageHeaderContent>
  );
}
